import React, { useState, useEffect, useCallback } from 'react';
import ErpTemplate from '../../components/ErpTemplate';
import api from '../../services/api';
import { FaChartBar, FaSave, FaClipboardList, FaCalendarAlt, FaUsers, FaInfoCircle, FaPaperclip, FaFileAlt, FaHashtag, FaMoneyBillAlt } from 'react-icons/fa';
import { data } from '../../util/util';
import { toast } from 'react-toastify';
import ReactSelect from '../../components/ReactSelect';
import { OrcamentoClientInheritance } from '../Orcamento/orcamentoClientInheritance';
import '../../css/financeiro-parcela-pagar.css';

export default function ResumoBackOffice({ match }) {
    const eventoId = match.params.id;
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem('perfil');
    const [dados, setDados] = useState({});
    const [loading, setLoading] = useState(true);
    const [salvando, setSalvando] = useState(false);

    const listarPos = (pos) => Array.isArray(pos) ? pos : [];
    const nomeArquivo = (arquivo) => (arquivo?.nome || '').split('\\').pop().split('/').pop();

    // Família Sanofi resolvida pelo helper canônico (não pela coluna `modelo` crua): sem ele Opella
    // e Medley, que herdam o modelo, ficam sem o card de BackOffice que sempre tiveram.
    const clienteFamiliaSanofi = OrcamentoClientInheritance.belongsToSanofiFamily({
        id: dados.cliente?.id,
        modelo: dados.modeloCliente,
        nome: dados.cliente?.nome,
    });

    // Apenas perfil backoffice e cliente da família Sanofi podem editar Identificação e Link no card Informações Básicas
    const podeEditarInformacoesBasicas = perfil === 'backoffice' && clienteFamiliaSanofi;

    // Modelo já resolvido pela herança, no mesmo formato do back (Opella e Medley respondem 'sanofi').
    const modeloFechamento = clienteFamiliaSanofi ? 'sanofi' : (dados.modeloCliente ?? 'master');

    // Cada item declara de quais clientes é (`clientes`) ou de quais não é (`exceto`); sem os dois, vale
    // para todos. Espelha DOCUMENTOS_FECHAMENTO/CAMPOS_FECHAMENTO no back — a aba genérica tem poucos
    // campos, e sem o filtro o Resumo dela mostraria uma dúzia de linhas vazias do Sanofi.
    const itemDoCliente = ({ clientes, exceto }) => (!clientes || clientes.includes(modeloFechamento))
        && !(exceto || []).includes(modeloFechamento);

    const buscarDados = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api({
                method: 'get',
                action: '/evento/' + eventoId,
                token: token,
            });

            if (response.data && response.data.data) {
                const dadosRecebidos = response.data.data;

                // Processar dados básicos
                const dadosProcessados = {
                    ...dadosRecebidos,
                    modeloCliente: dadosRecebidos.cliente?.modelo ?? 'master'
                };

                //Processar subeventos
                if (!Array.isArray(dadosRecebidos.subeventos))
                    dadosRecebidos.subeventos = [];
                dadosProcessados.subeventos = dadosRecebidos.subeventos.map(objeto => ({
                    value: objeto.id,
                    label: objeto.codigo + ' - ' + objeto.nome
                }));


                // Processar dados do backoffice
                if (dadosRecebidos.backoffice) {
                    const boWrapper = dadosRecebidos.backoffice;
                    const boData = boWrapper.dados_backoffice || {};
                    if (boWrapper.briefing_arquivo || boWrapper.briefingArquivo) {
                        boData.briefingArquivoObj = boWrapper.briefing_arquivo || boWrapper.briefingArquivo;
                    }
                    if (boWrapper.agenda_transparency_arquivo || boWrapper.agendaTransparencyArquivo) {
                        boData.agendaTransparencyArquivoObj = boWrapper.agenda_transparency_arquivo || boWrapper.agendaTransparencyArquivo;
                    }
                    if (boWrapper.agenda_oficial_pdf_arquivo || boWrapper.agendaOficialPdfArquivo) {
                        boData.agendaOficialPdfArquivoObj = boWrapper.agenda_oficial_pdf_arquivo || boWrapper.agendaOficialPdfArquivo;
                    }
                    if (boWrapper.verificacao_oi_arquivo || boWrapper.verificacaoOiArquivo) {
                        boData.verificacaoOiArquivoObj = boWrapper.verificacao_oi_arquivo || boWrapper.verificacaoOiArquivo;
                    }
                    if (boWrapper.dd_valido_arquivo || boWrapper.ddValidoArquivo) {
                        boData.ddValidoArquivoObj = boWrapper.dd_valido_arquivo || boWrapper.ddValidoArquivo;
                    }
                    if (boWrapper.proposta_comercial_arquivo || boWrapper.propostaComercialArquivo) {
                        boData.propostaComercialArquivoObj = boWrapper.proposta_comercial_arquivo || boWrapper.propostaComercialArquivo;
                    }
                    dadosProcessados.backoffice = boData;
                } else {
                    dadosProcessados.backoffice = {};
                }

                // Processar dados do backoffice fechamento
                if (dadosRecebidos.backoffice_fechamento || dadosRecebidos.backofficeFechamento) {
                    const bfWrapper = dadosRecebidos.backoffice_fechamento || dadosRecebidos.backofficeFechamento;
                    const bfData = bfWrapper.dados_backoffice_fechamento || {};
                    bfData.documentosDiversosSalvos = (bfWrapper.documentos_diversos_arquivos || [])
                        .filter((item) => item.arquivo?.id)
                        .map((item) => ({ id: item.arquivo.id, nome: item.arquivo.nome }));
                    bfData.listaPresencaSalvos = (bfWrapper.lista_presenca_arquivos || [])
                        .filter((item) => item.arquivo?.id)
                        .map((item) => ({ id: item.arquivo.id, nome: item.arquivo.nome }));
                    if (bfWrapper.lista_staff_sanofi_arquivo_obj) {
                        bfData.listaStaffSanofiArquivoObj = bfWrapper.lista_staff_sanofi_arquivo_obj;
                    }
                    if (bfWrapper.lista_staff_terceiros_arquivo_obj) {
                        bfData.listaStaffTerceirosArquivoObj = bfWrapper.lista_staff_terceiros_arquivo_obj;
                    }
                    if (bfWrapper.planilha_rsvp_arquivo_obj) {
                        bfData.planilhaRsvpArquivoObj = bfWrapper.planilha_rsvp_arquivo_obj;
                    }
                    if (bfWrapper.planilha_custo_final_arquivo_obj) {
                        bfData.planilhaCustoFinalArquivoObj = bfWrapper.planilha_custo_final_arquivo_obj;
                    }
                    if (bfWrapper.planilha_final_aereo_arquivo_obj) {
                        bfData.planilhaFinalAereoArquivoObj = bfWrapper.planilha_final_aereo_arquivo_obj;
                    }
                    if (bfWrapper.planilha_transfer_excel_arquivo_obj) {
                        bfData.planilhaTransferArquivoObj = bfWrapper.planilha_transfer_excel_arquivo_obj;
                    }
                    if (bfWrapper.rooming_list_excel_arquivo_obj) {
                        bfData.roomingListArquivoObj = bfWrapper.rooming_list_excel_arquivo_obj;
                    }
                    dadosProcessados.backoffice_fechamento = bfData;
                } else {
                    dadosProcessados.backoffice_fechamento = {};
                }

                setDados(dadosProcessados);
            }
        } catch (error) {
            console.error('Erro ao buscar dados:', error);
        } finally {
            setLoading(false);
        }
    }, [eventoId, token]);

    const salvarInformacoesBasicas = useCallback(async () => {
        if (!eventoId) return;
        setSalvando(true);
        try {
            await api({
                method: 'put',
                action: '/evento/' + eventoId,
                token: token,
                data: {
                    identificacao: dados.identificacao ?? '',
                    link: dados.link ?? '',
                },
            });
            toast.success('Informações básicas salvas com sucesso.');
            buscarDados();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erro ao salvar.');
        } finally {
            setSalvando(false);
        }
    }, [eventoId, token, dados.identificacao, dados.link, buscarDados]);

    useEffect(() => {
        buscarDados();
    }, [buscarDados]);

    if (loading) {
        return (
            <ErpTemplate>
                <div className="main-content container-fluid fin-pp">
                    <div style={{ textAlign: 'center', padding: 32 }}>
                        <div className="fin-pp__spinner" role="status" aria-label="Carregando" />
                        <p style={{ marginTop: 8 }}>Carregando dados...</p>
                    </div>
                </div>
            </ErpTemplate>
        );
    }

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h2 className="fin-pp__title">Resumo BackOffice</h2>
                        {dados.nome ? <p className="fin-pp__subtitle">{dados.nome}</p> : null}
                    </div>
                </div>

                {/* Primeira linha: Informações Básicas + BackOffice Abertura (quando aplicável) */}
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Informações Básicas */}
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-ic"><FaClipboardList size={15} /></span>
                                <span className="fin-pp__cphead-title">Informações Básicas</span>
                            </div>

                            <div style={{ marginTop: 12 }}>
                                <div style={{ marginBottom: 12 }}>
                                    <label style={{ fontWeight: 600, marginBottom: 6, display: 'block', fontSize: 13 }}>Subeventos</label>
                                    <ReactSelect
                                        isMulti
                                        value={dados.subeventos ?? []}
                                        onChange={(value) => {
                                            setDados({ ...dados, subeventos: value || [] });
                                            const subeventos = value.map((option) => option.value);
                                            api({
                                                method: 'put',
                                                action: '/evento/' + eventoId,
                                                token: token,
                                                data: { subeventos },
                                            }).catch(() => { toast.error("Erro ao salvar subeventos.") });
                                        }}
                                        loadOptions={(input) => {
                                            return api({
                                                method: 'get',
                                                action: '/evento',
                                                token: token,
                                                params: {
                                                    nome: input,
                                                    cliente: dados?.cliente?.id
                                                }
                                            })
                                                .then(response => {
                                                    let options = response.data.data.map((registro) => {
                                                        return {
                                                            value: registro.id,
                                                            label: registro.codigo + ' - ' + registro.nome,
                                                            disabled: registro.id == eventoId
                                                        };
                                                    });
                                                    return options;
                                                })
                                                .catch(function () {
                                                    return [];
                                                });
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Nome do Evento</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{dados.nome || '-'}</p>
                                </div>

                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Código</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.codigo || '-'}</p>
                                    </div>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Identificação</span>
                                        {podeEditarInformacoesBasicas ? (
                                            <input
                                                type="text"
                                                className="fin-pp__input"
                                                style={{ paddingLeft: 14, marginTop: 4 }}
                                                value={dados.identificacao ?? ''}
                                                onChange={e => setDados({ ...dados, identificacao: e.target.value })}
                                                placeholder="Identificação"
                                            />
                                        ) : (
                                            <p style={{ margin: 0, fontWeight: 500 }}>{dados.identificacao || '-'}</p>
                                        )}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Código EM</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.codigoEm || '-'}</p>
                                    </div>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Cliente</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.cliente?.nome || '-'}</p>
                                    </div>
                                </div>

                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Empresa</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{dados.empresa?.nome || '-'}</p>
                                </div>

                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Complexidade</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.complexidade || '-'}</p>
                                    </div>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Nacional ou Internacional</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.nacionalidade || '-'}</p>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Local</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.local || '-'}</p>
                                    </div>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Município</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.municipio?.nome || '-'}</p>
                                    </div>
                                </div>

                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Endereço</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{dados.endereco || '-'}</p>
                                </div>

                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Link</span>
                                    {podeEditarInformacoesBasicas ? (
                                        <input
                                            type="text"
                                            className="fin-pp__input"
                                            style={{ paddingLeft: 14, marginTop: 4 }}
                                            value={dados.link ?? ''}
                                            onChange={e => setDados({ ...dados, link: e.target.value })}
                                            placeholder="Link"
                                        />
                                    ) : (
                                        <p style={{ margin: 0, fontWeight: 500 }}>
                                            {dados.link ? (
                                                <a href={dados.link} target="_blank" rel="noopener noreferrer">{dados.link}</a>
                                            ) : '-'}
                                        </p>
                                    )}
                                </div>

                                {podeEditarInformacoesBasicas && (
                                    <div style={{ marginTop: 8 }}>
                                        <button
                                            type="button"
                                            className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm"
                                            onClick={salvarInformacoesBasicas}
                                            disabled={salvando}
                                        >
                                            <FaSave size={14} style={{ marginRight: 6 }} />
                                            {salvando ? 'Salvando...' : 'Salvar'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {clienteFamiliaSanofi && dados.backoffice ? (
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {/* BackOffice Abertura */}
                            <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                                <div className="fin-pp__cphead">
                                    <span className="fin-pp__cphead-ic"><FaChartBar size={15} /></span>
                                    <span className="fin-pp__cphead-title">PREVISÃO DE TIPOS DE EVENTOS</span>
                                </div>

                                <div style={{ marginTop: 12 }}>
                                    {[
                                        { label: 'Briefing (Utilizar o Atualizado)', obj: dados.backoffice.briefingArquivoObj },
                                        { label: 'Agenda Transparency', obj: dados.backoffice.agendaTransparencyArquivoObj },
                                        { label: 'Agenda Oficial PDF', obj: dados.backoffice.agendaOficialPdfArquivoObj },
                                        { label: 'Verificação OI', obj: dados.backoffice.verificacaoOiArquivoObj },
                                        { label: 'DD Válido', obj: dados.backoffice.ddValidoArquivoObj },
                                        { label: 'Proposta Comercial', obj: dados.backoffice.propostaComercialArquivoObj },
                                    ].map(({ label, obj }) => (
                                        <div key={label} style={{ marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>{label}</span>
                                            <p style={{ margin: 0, fontWeight: 500 }}>
                                                {obj?.id ? (
                                                    <a
                                                        href={`${process.env.REACT_APP_API_URL}/arquivo/download/${obj.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {obj.nome?.split('\\').pop().split('/').pop()}
                                                    </a>
                                                ) : (
                                                    <span style={{ color: 'var(--fin-muted)' }}>-</span>
                                                )}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* Segunda linha: Datas e Horários + Responsáveis */}
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Datas e Horários */}
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-ic"><FaCalendarAlt size={15} /></span>
                                <span className="fin-pp__cphead-title">Datas e Horários</span>
                            </div>

                            <div style={{ marginTop: 12 }}>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Data Inicial</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.data ? data(dados.data) : '-'}</p>
                                    </div>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Horário Inicial</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.hora || '-'}</p>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Data Final</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.dataFim ? data(dados.dataFim) : '-'}</p>
                                    </div>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Horário Final</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.horaFim || '-'}</p>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Replanejado Início</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.replanejadoInicio ? data(dados.replanejadoInicio) : '-'}</p>
                                    </div>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Replanejado Término</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.replanejadoFim ? data(dados.replanejadoFim) : '-'}</p>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Data de Competência</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.dataCompetencia ? data(dados.dataCompetencia) : '-'}</p>
                                    </div>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Data Limite Aceite</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.dataLimiteAceite ? data(dados.dataLimiteAceite) : '-'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Responsáveis */}
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-ic"><FaUsers size={15} /></span>
                                <span className="fin-pp__cphead-title">Responsáveis</span>
                            </div>

                            <div style={{ marginTop: 12 }}>
                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Responsável na Agência</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>
                                        {dados.responsavel && Array.isArray(dados.responsavel) && dados.responsavel.length > 0
                                            ? dados.responsavel.map(r => r.nome || r.label || r).join(', ')
                                            : '-'}
                                    </p>
                                </div>
                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Diretor Responsável</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{dados.diretorresponsavel?.nome || '-'}</p>
                                </div>
                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Coordenador RSVP</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{dados.coordenadorrsvp?.nome || '-'}</p>
                                </div>
                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Responsável no Cliente (Focal Point Eventos)</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>
                                        {dados.responsavelcliente && Array.isArray(dados.responsavelcliente) && dados.responsavelcliente.length > 0
                                            ? dados.responsavelcliente.map(r => r.nome || r.label || r).join(', ')
                                            : '-'}
                                    </p>
                                </div>
                                {clienteFamiliaSanofi && (
                                    <>
                                        <div style={{ marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Requisitante BU (Solicitante do evento Sanofi)</span>
                                            <p style={{ margin: 0, fontWeight: 500 }}>
                                                {dados.backoffice?.requisitanteBu || '-'}
                                            </p>
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Aprovador BU</span>
                                            <p style={{ margin: 0, fontWeight: 500 }}>
                                                {dados.backoffice?.aprovadorBu || '-'}
                                            </p>
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Aprovador Médico</span>
                                            <p style={{ margin: 0, fontWeight: 500 }}>
                                                {dados.backoffice?.aprovadorMedico || '-'}
                                            </p>
                                        </div>
                                        {dados.backoffice?.outros && (
                                            <div style={{ marginBottom: 10 }}>
                                                <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Outros</span>
                                                <p style={{ margin: 0, fontWeight: 500 }}>
                                                    {dados.backoffice?.outros || '-'}
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Terceira linha: Informações do Evento + Arquivos do Evento */}
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Informações do Evento */}
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-ic"><FaInfoCircle size={15} /></span>
                                <span className="fin-pp__cphead-title">Informações do Evento</span>
                            </div>

                            <div style={{ marginTop: 12 }}>
                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Tipo</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{dados.tipo || '-'}</p>
                                </div>
                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Formato</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{dados.tipoRealizacao || '-'}</p>
                                </div>
                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Tipo de Lista</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{dados.tipoIngresso || '-'}</p>
                                </div>
                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Quarter</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>{dados.quarter || '-'}</p>
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Quantidade de Pessoas</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.quantidadePessoas || '-'}</p>
                                    </div>
                                    <div style={{ flex: 1, marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Quantidade de Aéreo</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>{dados.quantidadeAereo || '-'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Arquivos do Evento */}
                        <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-ic"><FaPaperclip size={15} /></span>
                                <span className="fin-pp__cphead-title">Arquivos do Evento</span>
                            </div>

                            <div style={{ marginTop: 12 }}>
                                {/* Logomarca */}
                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Logomarca</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>
                                        {dados.logomarca?.id ? (
                                            <a
                                                href={`${process.env.REACT_APP_API_URL}/arquivo/download/${dados.logomarca.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {dados.logomarca.nome?.split('\\').pop().split('/').pop() || 'Baixar Logomarca'}
                                            </a>
                                        ) : (
                                            <span style={{ color: 'var(--fin-muted)' }}>-</span>
                                        )}
                                    </p>
                                </div>

                                {/* Imagem de Fundo */}
                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Imagem de Fundo</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>
                                        {dados.imagemfundo?.id ? (
                                            <a
                                                href={`${process.env.REACT_APP_API_URL}/arquivo/download/${dados.imagemfundo.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {dados.imagemfundo.nome?.split('\\').pop().split('/').pop() || 'Baixar Imagem de Fundo'}
                                            </a>
                                        ) : (
                                            <span style={{ color: 'var(--fin-muted)' }}>-</span>
                                        )}
                                    </p>
                                </div>

                                {/* P.O.s */}
                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>P.O.s</span>
                                    {listarPos(dados.pos).length > 0 ? (
                                        <div>
                                            {listarPos(dados.pos).map((po, index) => (
                                                <div key={po.id || `po-${index}`} style={{ marginTop: index > 0 ? 8 : 4 }}>
                                                    <div><strong>Número:</strong> {po.numero || '-'}</div>
                                                    <div>
                                                        <strong>Arquivo:</strong>{' '}
                                                        {po.arquivo?.id ? (
                                                            <a
                                                                href={`${process.env.REACT_APP_API_URL}/arquivo/download/${po.arquivo.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {nomeArquivo(po.arquivo) || 'Baixar Arquivo P.O'}
                                                            </a>
                                                        ) : (
                                                            <span style={{ color: 'var(--fin-muted)' }}>Sem arquivo</span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p style={{ margin: 0, fontWeight: 500, color: 'var(--fin-muted)' }}>-</p>
                                    )}
                                </div>

                                {/* Arquivo Agenda */}
                                <div style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Arquivo Agenda</span>
                                    <p style={{ margin: 0, fontWeight: 500 }}>
                                        {dados.arquivoagenda?.id ? (
                                            <a
                                                href={`${process.env.REACT_APP_API_URL}/arquivo/download/${dados.arquivoagenda.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {dados.arquivoagenda.nome?.split('\\').pop().split('/').pop() || 'Baixar Arquivo Agenda'}
                                            </a>
                                        ) : (
                                            <span style={{ color: 'var(--fin-muted)' }}>-</span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* BackOffice Fechamento: todo cliente tem o seu; a família Sanofi, com os campos próprios */}
                {dados.backoffice_fechamento && (
                    <>
                        {/* Quinta linha: Dados Gerais + Quantidades — campos que só a aba Sanofi preenche
                            (Focal Sanofi, Staff Sanofi, ID Sistema, Links, Tipo de Evento). */}
                        {clienteFamiliaSanofi && (
                        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                {/* Dados Gerais */}
                                <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                                    <div className="fin-pp__cphead">
                                        <span className="fin-pp__cphead-ic"><FaClipboardList size={15} /></span>
                                        <span className="fin-pp__cphead-title">Dados Gerais</span>
                                    </div>

                                    <div style={{ marginTop: 12 }}>
                                        <div style={{ marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>ID Sistema</span>
                                            <p style={{ margin: 0, fontWeight: 500 }}>{dados.backoffice_fechamento?.id_sistema || '-'}</p>
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Focal Sanofi</span>
                                            <p style={{ margin: 0, fontWeight: 500 }}>{dados.backoffice_fechamento?.focal_sanofi || '-'}</p>
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Link 1</span>
                                            <p style={{ margin: 0, fontWeight: 500 }}>
                                                {dados.backoffice_fechamento?.link1 ? (
                                                    <a href={dados.backoffice_fechamento.link1} target="_blank" rel="noopener noreferrer">{dados.backoffice_fechamento.link1}</a>
                                                ) : '-'}
                                            </p>
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Link 2 (Opcional)</span>
                                            <p style={{ margin: 0, fontWeight: 500 }}>
                                                {dados.backoffice_fechamento?.link2 ? (
                                                    <a href={dados.backoffice_fechamento.link2} target="_blank" rel="noopener noreferrer">{dados.backoffice_fechamento.link2}</a>
                                                ) : '-'}
                                            </p>
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Link 3 (Opcional)</span>
                                            <p style={{ margin: 0, fontWeight: 500 }}>
                                                {dados.backoffice_fechamento?.link3 ? (
                                                    <a href={dados.backoffice_fechamento.link3} target="_blank" rel="noopener noreferrer">{dados.backoffice_fechamento.link3}</a>
                                                ) : '-'}
                                            </p>
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Tipo de Evento</span>
                                            <p style={{ margin: 0, fontWeight: 500 }}>
                                                {dados.backoffice_fechamento?.tipo_evento_lista_previa && 'Lista Prévia'}
                                                {dados.backoffice_fechamento?.tipo_evento_lista_previa && dados.backoffice_fechamento?.tipo_evento_adesao && ', '}
                                                {dados.backoffice_fechamento?.tipo_evento_adesao && 'Adesão'}
                                                {!dados.backoffice_fechamento?.tipo_evento_lista_previa && !dados.backoffice_fechamento?.tipo_evento_adesao && '-'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                                {/* Quantidades */}
                                <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                                    <div className="fin-pp__cphead">
                                        <span className="fin-pp__cphead-ic"><FaHashtag size={15} /></span>
                                        <span className="fin-pp__cphead-title">Quantidades</span>
                                    </div>

                                    <div style={{ marginTop: 12 }}>
                                        {[
                                            { label: 'Quantidade de Pax Convidados HCP', key: 'quantidade_pax_hcp' },
                                            { label: 'Quantidade de Pax Convidados NO HCP', key: 'quantidade_pax_no_hcp' },
                                            { label: 'Quantidade Staff Sanofi', key: 'quantidade_staff_sanofi' },
                                        ].map(({ label, key }) => (
                                            <div key={key} style={{ marginBottom: 10 }}>
                                                <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>{label}</span>
                                                <p style={{ margin: 0, fontWeight: 500 }}>{dados.backoffice_fechamento?.[key] || '-'}</p>
                                            </div>
                                        ))}
                                        <div style={{ marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Nomes Staff Sanofi</span>
                                            <p style={{ margin: 0, fontWeight: 500, whiteSpace: 'pre-wrap' }}>{dados.backoffice_fechamento?.nomes_staff_sanofi || '-'}</p>
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Quantidade Staff Terceiro</span>
                                            <p style={{ margin: 0, fontWeight: 500 }}>{dados.backoffice_fechamento?.quantidade_staff_terceiro || '-'}</p>
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Nomes Staff Terceiro</span>
                                            <p style={{ margin: 0, fontWeight: 500, whiteSpace: 'pre-wrap' }}>{dados.backoffice_fechamento?.nomes_staff_terceiro || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Sexta linha: Arquivos Obrigatórios + Arquivos Obrigatórios para Inclusões de Custo */}
                        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                            {clienteFamiliaSanofi && (
                            <div style={{ flex: 1, minWidth: 0 }}>
                                {/* Arquivos Obrigatórios — todos exclusivos da aba Sanofi */}
                                <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                                    <div className="fin-pp__cphead">
                                        <span className="fin-pp__cphead-ic"><FaPaperclip size={15} /></span>
                                        <span className="fin-pp__cphead-title">Arquivos Obrigatórios</span>
                                    </div>

                                    <div style={{ marginTop: 12 }}>
                                        {[
                                            // Lista de presença é N arquivos (entidade fraca) — vem em `objs`, os demais seguem 1:1 em `obj`
                                            { label: 'Lista de presença', objs: dados.backoffice_fechamento?.listaPresencaSalvos, naoAplica: dados.backoffice_fechamento?.nao_se_aplica_lista_presenca },
                                            // As listas de staff podem ser fechadas sem arquivo, via "Anexou" + justificativa: sem
                                            // o texto aqui, esse caminho ficaria igual a "não preencheu nada" para quem confere.
                                            { label: 'Lista de Staff Sanofi', obj: dados.backoffice_fechamento?.listaStaffSanofiArquivoObj, naoAplica: dados.backoffice_fechamento?.nao_se_aplica_lista_staff_sanofi, texto: dados.backoffice_fechamento?.lista_staff_sanofi_texto },
                                            { label: 'Lista de Staff terceiros (TODOS)', obj: dados.backoffice_fechamento?.listaStaffTerceirosArquivoObj, naoAplica: dados.backoffice_fechamento?.nao_se_aplica_lista_staff_terceiros, texto: dados.backoffice_fechamento?.lista_staff_terceiros_texto },
                                            { label: 'Planilha de RSVP (Quando realizado)', obj: dados.backoffice_fechamento?.planilhaRsvpArquivoObj, naoAplica: dados.backoffice_fechamento?.nao_se_aplica_planilha_rsvp },
                                        ].map(({ label, obj, objs, naoAplica, texto }) => (
                                            <div key={label} style={{ marginBottom: 10 }}>
                                                <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>
                                                    {label}
                                                    {naoAplica && <span style={{ marginLeft: 6 }}>(Não se aplica)</span>}
                                                </span>
                                                <p style={{ margin: 0, fontWeight: 500 }}>
                                                    {(objs || (obj?.id ? [obj] : [])).length ? (
                                                        (objs || [obj]).map((arq) => (
                                                            <a
                                                                key={arq.id}
                                                                href={`${process.env.REACT_APP_API_URL}/arquivo/download/${arq.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ display: 'block' }}
                                                            >
                                                                {arq.nome?.split('\\').pop().split('/').pop()}
                                                            </a>
                                                        ))
                                                    ) : texto ? (
                                                        <span style={{ whiteSpace: 'pre-wrap' }}>Anexou: {texto}</span>
                                                    ) : (
                                                        <span style={{ color: 'var(--fin-muted)' }}>-</span>
                                                    )}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            )}

                            <div style={{ flex: 1, minWidth: 0 }}>
                                {/* Arquivos Obrigatórios para Inclusões de Custo */}
                                <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                                    <div className="fin-pp__cphead">
                                        <span className="fin-pp__cphead-ic"><FaMoneyBillAlt size={15} /></span>
                                        <span className="fin-pp__cphead-title">Arquivos Obrigatórios para Inclusões de Custo no Sistema</span>
                                    </div>

                                    <div style={{ marginTop: 12 }}>
                                        {[
                                            // Fora da Sanofi não há o card dos obrigatórios: a lista de presença da aba genérica sai aqui.
                                            { label: 'Lista de presença', objs: dados.backoffice_fechamento?.listaPresencaSalvos, naoAplica: dados.backoffice_fechamento?.nao_se_aplica_lista_presenca, exceto: ['sanofi'] },
                                            { label: 'Planilha de custo Final aprovada', obj: dados.backoffice_fechamento?.planilhaCustoFinalArquivoObj, naoAplica: dados.backoffice_fechamento?.nao_se_aplica_planilha_custo_final, clientes: ['sanofi'] },
                                            { label: 'Planilha final de aéreo (Eventos com hospitalidade)', obj: dados.backoffice_fechamento?.planilhaFinalAereoArquivoObj, naoAplica: dados.backoffice_fechamento?.nao_se_aplica_planilha_final_aereo, clientes: ['sanofi'] },
                                            { label: 'Planilha de Transfer em Excel (Eventos com hospitalidade)', obj: dados.backoffice_fechamento?.planilhaTransferArquivoObj, naoAplica: dados.backoffice_fechamento?.nao_se_aplica_planilha_transfer },
                                            { label: 'Rooming List em Excel (Eventos com hospitalidade)', obj: dados.backoffice_fechamento?.roomingListArquivoObj, naoAplica: dados.backoffice_fechamento?.nao_se_aplica_rooming_list },
                                            // N arquivos (entidade fraca), como a Lista de presença — vem em `objs`. "BackOffice" no
                                            // rótulo porque esta tela já mostra os Documentos Diversos do RSVP, que são outro campo.
                                            { label: 'Documentos Diversos BackOffice', objs: dados.backoffice_fechamento?.documentosDiversosSalvos, exceto: ['sanofi'] },
                                        ].filter(itemDoCliente).map(({ label, obj, objs, naoAplica }) => (
                                            <div key={label} style={{ marginBottom: 10 }}>
                                                <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>
                                                    {label}
                                                    {naoAplica && <span style={{ marginLeft: 6 }}>(Não se aplica)</span>}
                                                </span>
                                                <p style={{ margin: 0, fontWeight: 500 }}>
                                                    {(objs || (obj?.id ? [obj] : [])).length ? (
                                                        (objs || [obj]).map((arq) => (
                                                            <a
                                                                key={arq.id}
                                                                href={`${process.env.REACT_APP_API_URL}/arquivo/download/${arq.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ display: 'block' }}
                                                            >
                                                                {arq.nome?.split('\\').pop().split('/').pop()}
                                                            </a>
                                                        ))
                                                    ) : (
                                                        <span style={{ color: 'var(--fin-muted)' }}>-</span>
                                                    )}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Arquivos do RSVP - Última seção */}
                {dados.evento_rsvp_data && (dados.rsvp ?? 'nao') === 'sim' && (
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {/* Arquivos do RSVP */}
                            <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                                <div className="fin-pp__cphead">
                                    <span className="fin-pp__cphead-ic"><FaFileAlt size={15} /></span>
                                    <span className="fin-pp__cphead-title">Arquivos do RSVP</span>
                                </div>

                                <div style={{ marginTop: 12 }}>
                                    {/* Planilha de Convidados */}
                                    <div style={{ marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Planilha de Convidados</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>
                                            {dados.evento_rsvp_data?.planilha_arquivo?.id ? (
                                                <a
                                                    href={`${process.env.REACT_APP_API_URL}/arquivo/download/${dados.evento_rsvp_data.planilha_arquivo.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {dados.evento_rsvp_data.planilha_arquivo.nome?.split('\\').pop().split('/').pop() || 'Baixar Planilha'}
                                                </a>
                                            ) : (
                                                <span style={{ color: 'var(--fin-muted)' }}>N/A</span>
                                            )}
                                        </p>
                                    </div>

                                    {/* Malha Aérea */}
                                    <div style={{ marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Malha Aérea</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>
                                            {dados.evento_rsvp_data?.malha_aerea_arquivo?.id ? (
                                                <a
                                                    href={`${process.env.REACT_APP_API_URL}/arquivo/download/${dados.evento_rsvp_data.malha_aerea_arquivo.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {dados.evento_rsvp_data.malha_aerea_arquivo.nome?.split('\\').pop().split('/').pop() || 'Baixar Malha Aérea'}
                                                </a>
                                            ) : (
                                                <span style={{ color: 'var(--fin-muted)' }}>N/A</span>
                                            )}
                                        </p>
                                    </div>

                                    <div style={{ marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Agenda Prévia</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>
                                            {dados.evento_rsvp_data?.agenda_previa_arquivo?.id ? (
                                                <a
                                                    href={`${process.env.REACT_APP_API_URL}/arquivo/download/${dados.evento_rsvp_data.agenda_previa_arquivo.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {dados.evento_rsvp_data.agenda_previa_arquivo.nome?.split('\\').pop().split('/').pop() || 'Baixar Agenda Prévia'}
                                                </a>
                                            ) : (
                                                <span style={{ color: 'var(--fin-muted)' }}>-</span>
                                            )}
                                        </p>
                                    </div>

                                    {/* Arquivo Controle de Cotas */}
                                    <div style={{ marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Arquivo Controle de Cotas</span>
                                        <p style={{ margin: 0, fontWeight: 500 }}>
                                            {dados.evento_rsvp_data?.arquivo_controle_cotas?.id ? (
                                                <a
                                                    href={`${process.env.REACT_APP_API_URL}/arquivo/download/${dados.evento_rsvp_data.arquivo_controle_cotas.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {dados.evento_rsvp_data.arquivo_controle_cotas.nome?.split('\\').pop().split('/').pop() || 'Baixar Arquivo Controle de Cotas'}
                                                </a>
                                            ) : dados.evento_rsvp_data?.observacoes_controle_cotas === 'N/A' ? (
                                                <span style={{ color: 'var(--fin-muted)' }}>N/A</span>
                                            ) : (
                                                <span className="fin-pp__pill is-warn">A definir</span>
                                            )}
                                        </p>
                                    </div>

                                    {/* Documentos Diversos */}
                                    <div style={{ marginBottom: 10 }}>
                                        <span style={{ fontSize: 12, color: 'var(--fin-muted)' }}>Documentos Diversos</span>
                                        {dados.evento_rsvp_data?.documentos_diversos_arquivos && Array.isArray(dados.evento_rsvp_data.documentos_diversos_arquivos) && dados.evento_rsvp_data.documentos_diversos_arquivos.length > 0 ? (
                                            <div>
                                                {dados.evento_rsvp_data.documentos_diversos_arquivos.map((arquivo, index) => (
                                                    <p key={index} style={{ margin: '4px 0 0', fontWeight: 500 }}>
                                                        <a
                                                            href={`${process.env.REACT_APP_API_URL}/arquivo/download/${arquivo.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {arquivo.nome?.split('\\').pop().split('/').pop() || `Documento ${index + 1}`}
                                                        </a>
                                                    </p>
                                                ))}
                                            </div>
                                        ) : (
                                            <p style={{ margin: 0, fontWeight: 500, color: 'var(--fin-muted)' }}>-</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </ErpTemplate>
    );
}
